import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, ExternalLink, Rocket, ShieldCheck, Sparkles, Calendar,
  Clock, FileText, Quote, ArrowRight, CheckCircle2, ArrowLeft,
} from "lucide-react";
import { launchShowcaseDetail } from "../api";

/**
 * /launch/showcase/{slug} — one live customer site as a full case-study
 * page: hero screenshot, metrics, feature highlights, quote, related
 * vertical design (deep-link back into the design board), CTA.
 */
export default function LaunchShowcaseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    launchShowcaseDetail(slug)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Case study not found"));
  }, [slug]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="p-8 max-w-md text-center">
        <div className="text-lg font-bold text-slate-900">Case study not found</div>
        <div className="text-sm text-slate-500 mt-1">{err}</div>
        <Link to="/launch/showcase">
          <Button className="mt-5 bg-slate-900 text-white hover:bg-slate-800">
            <ArrowLeft size={14} className="mr-1" /> All case studies
          </Button>
        </Link>
      </Card>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-sky-600" size={30} />
    </div>
  );

  const { site, related } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-sky-50/60 to-white text-slate-900"
         data-testid="launch-showcase-detail-page">
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <Link to="/launch/showcase" data-testid="showcase-detail-back"
              className="text-xs text-slate-500 hover:text-sky-700 font-semibold inline-flex items-center gap-1">
          <ArrowLeft size={12} /> All case studies
        </Link>

        <div className="mt-4 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <Badge className="bg-emerald-100 border border-emerald-300 text-emerald-800 uppercase tracking-widest text-[10px] font-bold">
              <ShieldCheck size={11} className="mr-1" /> Live customer site
            </Badge>
            <h1 className="mt-4 text-4xl sm:text-5xl font-black text-slate-900" data-testid="showcase-detail-name">
              {site.name}
            </h1>
            <div className="mt-1 text-xs uppercase tracking-widest text-slate-500 font-bold">{site.vertical_label}</div>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">{site.blurb}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={site.url} target="_blank" rel="noreferrer" data-testid="showcase-visit">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                  Visit the live site <ExternalLink size={13} className="ml-1" />
                </Button>
              </a>
              <Link to="/launch">
                <Button variant="outline" className="border-sky-300 text-sky-800 bg-white">
                  <Rocket size={13} className="mr-1" /> Start my free AI preview
                </Button>
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 text-sm">
              <Fact icon={Calendar} label="Launched" value={site.launched_at} testid="fact-launched" />
              <Fact icon={Clock} label="Time to launch" value={`${site.time_to_launch_days} days`} testid="fact-time" />
              <Fact icon={FileText} label="Pages" value={String(site.page_count)} testid="fact-pages" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-white">
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img src={site.screenshot} alt={`${site.name} homepage`}
                     className="w-full h-full object-cover object-top"
                     data-testid="showcase-detail-screenshot" />
              </div>
            </div>
          </div>
        </div>

        {site.metrics?.length > 0 && (
          <div className="mt-14">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Results</div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              {site.metrics.map((m) => (
                <Card key={m.label} className="p-5 bg-white border-slate-200 shadow-sm" data-testid={`showcase-metric-${m.label.replace(/[^a-z0-9]+/gi, '-')}`}>
                  <div className="text-3xl font-black bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">{m.value}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mt-1">{m.label}</div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {site.highlights?.length > 0 && (
          <div className="mt-14">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">What we built</div>
            <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
              {site.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 bg-white rounded-lg border border-slate-200 p-3">
                  <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {site.quote && (
          <div className="mt-14">
            <Card className="p-8 bg-gradient-to-br from-sky-50 to-emerald-50 border-sky-200">
              <Quote size={26} className="text-sky-600" />
              <p className="mt-3 text-xl italic text-slate-800 leading-relaxed" data-testid="showcase-quote-text">
                “{site.quote.text}”
              </p>
              <div className="mt-4 text-sm font-bold text-slate-700">— {site.quote.attribution}</div>
            </Card>
          </div>
        )}

        {related?.mockup_image && (
          <div className="mt-14">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Related design</div>
            <div className="mt-3 grid md:grid-cols-2 gap-6 items-center bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div>
                <div className="text-lg font-bold text-slate-900">Want a site like {site.name}?</div>
                <div className="text-sm text-slate-600 mt-1">
                  We build it starting from the <span className="font-semibold">{related.vertical_label || "matching"}</span> design direction —
                  adapted to your business, copy, colors, and page plan.
                </div>
                <Link to={`/launch?vertical=${related.vertical}`}
                      data-testid="showcase-detail-start-similar">
                  <Button className="mt-4 bg-slate-900 hover:bg-slate-800 text-white">
                    <Sparkles size={13} className="mr-1" /> Start a site like this <ArrowRight size={13} className="ml-1" />
                  </Button>
                </Link>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200">
                <img src={related.mockup_image} alt="Related design"
                     className="w-full h-auto block" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        <Icon size={11} /> {label}
      </div>
      <div className="mt-1 text-slate-900 font-bold text-sm">{value}</div>
    </div>
  );
}
